import { i18n } from '@/i18n/settings'
import { Config } from '@/Config'
import router from '@/router'

export function viteConfig(key, { defaultValue = undefined, parse = true } = {}) {
  const raw = import.meta.env['VITE_' + key];
  if (raw === undefined) return defaultValue;

  if (!parse) return raw;

  // try boolean
  if (raw === 'true') return true;
  if (raw === 'false') return false;

  // try number (safe)
  const num = Number(raw);
  if (!Number.isNaN(num) && raw.trim() !== '') return num;

  // try JSON
  try {
    return JSON.parse(raw);
  } catch (e) {
    // not JSON -> return original string
    return raw;
  }
}

export function isValidNumber(value) {
  if (value === '' || value == null) return true
  if (typeof value === 'number') return Number.isFinite(value)
  const s = String(value).trim()
  if (s === '') return true
  return !Number.isNaN(Number(s))
}

export function formatSportTitle(sportKey) {
  if (!sportKey) return '';
  const translationKey = `events.sports.${sportKey}`;
  const translatedTitle = i18n.global.t(translationKey);
  if (translatedTitle !== translationKey) {
    return translatedTitle;
  }
  return sportKey.replace(/_/g, ' ').toUpperCase();
}

export function setPageTitle(title) {
  const baseTitle = viteConfig('APP_NAME');
  if (typeof document !== 'undefined'){
    document.title = title ? `${title} | ${baseTitle}` : baseTitle;
  }
}

export function buildQueryString(params, keys = null) {
  const queryParams = [];
  const keysToProcess = keys || Object.keys(params);
  
  for (const key of keysToProcess) {
    if (params[key]) {
      queryParams.push(`${key}=${encodeURIComponent(params[key])}`);
    }
  }
  
  return queryParams.length > 0 ? '?' + queryParams.join('&') : '';
}

/**
 * Navigate to a tournament page with optional filter params.
 * Params are only added if specified.
 * @param {string} slug
 * @param {object} [params] - { s, l, st, g } (season, level, stage, group numbers)
 * @param {string} [routeName] - route name (default: 'tournament-show')
 */
export function goToTournament(slug, params = {}, routeName = 'tournament-show') {
  const query = {}
  for (const [key, val] of Object.entries(params)) {
    if (val != null) {
      query[key] = val
    }
  }
  router.push({ name: routeName, params: { slug }, query })
}

export function getTournamentTypeIcon(type) {
  switch (type) {
    case 'round_robin':
      return 'mdi-calendar-multiselect';
    case 'knockout':
      return 'mdi-tournament';
    case 'swiss':
      return 'mdi-chess-king';
    default:
      return 'mdi-help-circle';
  }
}

export function getRoundIcon(state) {
  switch (state) {
    case 'scheduled':
      return 'mdi-calendar-clock'
    case 'completed':
      return 'mdi-check-circle-outline'
    case 'running':
      return 'mdi-progress-clock'
    case 'cancelled':
      return 'mdi-cancel'
    default:
      return 'mdi-help-circle-outline'
  }
}

export function getRoundColor(state) {
  switch (state) {
    case 'scheduled':
      return 'grey'
    case 'completed':
      return 'green'
    case 'running':
      return 'info'
    default:
      return 'grey'
  }
}

/**
 * Returns a sorted copy of the entries array based on tournament settings.
 * @param {Array} entries - Array of group entry objects to sort.
 * @param {Object} tournament - Tournament object (plain, not a ref).
 * @param {Object} allEntriesMap - Map of entry_id -> player data, used as fallback for missing fields.
 * @returns {Array} Sorted copy of entries.
 */
export function sortStandingEntries(entries, tournament, allEntriesMap = {}) {
  const tournamentSettings = tournament?.settings || {}
  let standingsSortingFields = tournamentSettings.standingsSortingFields || null

  if (!standingsSortingFields) {
    standingsSortingFields = Config.tournament.standingsColumns
      .filter(col => col.enabled)
      .map(col => ({ key: col.key, sortingType: col.sortingType || 'desc' }))
  }

  // Always add name as last sorting field
  standingsSortingFields = [...standingsSortingFields, { key: 'name', sortingType: 'asc' }]

  const coerce = (v) => {
    if (v === undefined || v === null) return v
    if (typeof v === 'number') return v
    if (typeof v === 'string') {
      const s = v.trim()
      if (s === '') return ''
      const n = Number(s)
      if (!Number.isNaN(n)) return n
      return s.toLowerCase()
    }
    return v
  }

  return [...entries].sort((a, b) => {
    for (const field of standingsSortingFields) {
      const key = field.key
      const type = field.sortingType || 'desc'

      let aValue = a[key]
      let bValue = b[key]

      if ((aValue === undefined || aValue === null) && a.entry_id) {
        aValue = (allEntriesMap[a.entry_id] || {})[key]
      }
      if ((bValue === undefined || bValue === null) && b.entry_id) {
        bValue = (allEntriesMap[b.entry_id] || {})[key]
      }

      aValue = coerce(aValue) ?? ''
      bValue = coerce(bValue) ?? ''

      if (aValue < bValue) return type === 'asc' ? -1 : 1
      if (aValue > bValue) return type === 'asc' ? 1 : -1
    }
    return 0
  })
}

export function seasonName(season) {
  if (!season) return ''
  const { t } = i18n.global
  return season.name || t('label.season_n', { n: season.season_number })
}

export function levelName(level) {
  const { t } = i18n.global
  return level.name || t('label.level_n', { n: level.level_number })
}

export function stageName(stage) {
  const { t } = i18n.global
  return stage.name || t('label.stage_n', { n: stage.stage_number })
}

export function groupName(group) {
  const { t } = i18n.global
  return group.name || t('label.group_n', { n: group.id })
}

export function getCurrentSeason(seasons) {
  return seasons
    .filter(s => s.state === 'running')
    .reduce((max, season) => season.season_number > (max?.season_number ?? -Infinity) ? season : max, null)
}